import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Menu, X, Film, Tv, Sparkles, User, LayoutDashboard, LogOut, ChevronDown } from 'lucide-react';
import { Container } from '../styled/Container';
import { regionFromPathname } from '../../utils/regionFromPath';
import { pathWithRegion } from '../../utils/navBase';
import { similarTypePath } from '../../utils/categoriesPaths';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser } from '../../store/reducers/authReducer';
import { useI18n } from '../../hooks/useI18n';
import { PUBLIC_REGION_CODES } from '../../config/publicRegions';

const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 50;
  background: ${({ theme }) => theme.colors.primary};
`;

const NavInner = styled(Container)`
  display: flex;
  height: 4rem;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  flex-shrink: 0;
`;

const LogoBox = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.gold};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.goldForeground};
`;

const LogoText = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primaryForeground};
  letter-spacing: -0.02em;
  @media (max-width: 1100px) {
    display: none;
  }
`;

const DesktopMid = styled.div`
  display: none;
  @media (min-width: 900px) {
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    gap: 0.125rem;
    min-width: 0;
    flex-wrap: wrap;
  }
`;

const NavLink = styled(Link)`
  padding: 0.45rem 0.65rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 600;
  white-space: nowrap;
  text-decoration: none;
  transition: all 0.2s;
  background: ${({ $active }) => ($active ? 'rgba(255,255,255,0.18)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : 'rgba(255,255,255,0.78)')};
  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.12);
  }
  @media (min-width: 1100px) {
    padding: 0.5rem 0.85rem;
    font-size: ${({ theme }) => theme.fontClamp.sm};
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
  margin-left: auto;
`;

const DesktopOnly = styled.div`
  display: none;
  @media (min-width: 900px) {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }
`;

const RegionWrap = styled.div`
  position: relative;
`;

const RegionBtn = styled.button`
  height: 2.5rem;
  border: none;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 0.65rem;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
`;

const Flag = styled.img`
  width: 16px;
  height: 12px;
  object-fit: cover;
  border-radius: 2px;
`;

const RegionMenu = styled(motion.div)`
  position: absolute;
  right: 0;
  top: calc(100% + 0.45rem);
  width: 180px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCardHover};
  padding: 0.35rem;
  z-index: 20;
`;

const RegionItem = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  border-radius: ${({ theme }) => theme.radiusSm};
  padding: 0.5rem 0.55rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
  &:hover {
    background: ${({ theme }) => theme.colors.muted};
  }
`;

const NavActionBtn = styled(Link)`
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 600;
  transition: all 0.2s;
  background: ${({ theme }) => theme.colors.gold};
  color: ${({ theme }) => theme.colors.goldForeground};
  text-decoration: none;
  &:hover {
    opacity: 0.9;
  }
`;

const SearchBtn = styled.button`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  border: none;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: ${({ theme }) => theme.colors.gold};
    color: ${({ theme }) => theme.colors.goldForeground};
  }
`;

const SearchPanel = styled(motion.div)`
  overflow: hidden;
  background: ${({ theme }) => theme.colors.primary};
`;

const SearchForm = styled.form`
  padding-bottom: 1rem;
`;

const SearchInputWrap = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 3rem;
  padding-left: 2.75rem;
  padding-right: 6.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: #fff;
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  border: 2px solid transparent;
  outline: none;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.gold};
  }
`;

const SearchSubmit = styled.button`
  position: absolute;
  right: 0.35rem;
  top: 0.35rem;
  bottom: 0.35rem;
  padding: 0 1.25rem;
  border: none;
  border-radius: ${({ theme }) => theme.radius};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  cursor: pointer;
`;

const UserMenuWrap = styled.div`
  position: relative;
`;

const UserMenuBtn = styled.button`
  height: 2.5rem;
  min-width: 2.5rem;
  padding: 0 0.35rem 0 0.25rem;
  border-radius: 9999px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  &:hover {
    background: rgba(255, 255, 255, 0.22);
  }
`;

const UserAvatar = styled.span`
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  font-size: 0.75rem;
  font-weight: 800;
  flex-shrink: 0;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserMenuPanel = styled(motion.div)`
  position: absolute;
  right: 0;
  top: calc(100% + 0.45rem);
  min-width: 220px;
  max-width: min(280px, calc(100vw - 1.5rem));
  background: #fff;
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCardHover};
  padding: 0.35rem;
  z-index: 40;
`;

const UserMenuHeader = styled.div`
  padding: 0.45rem 0.55rem 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 0.25rem;
`;

const UserMenuName = styled.div`
  font-weight: 800;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.foreground};
  line-height: 1.25;
  word-break: break-word;
`;

const UserMenuSub = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.15rem;
`;

const UserMenuRow = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.55rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  text-decoration: none;
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 600;
  border: none;
  background: transparent;
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.colors.muted};
  }
`;

const UserMenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.55rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 600;
  text-align: left;
  &:hover {
    background: ${({ theme }) => theme.colors.muted};
  }
`;

const MobileToggle = styled.button`
  @media (min-width: 900px) {
    display: none;
  }
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;
`;

const MobilePanel = styled(motion.div)`
  @media (min-width: 900px) {
    display: none;
  }
  background: ${({ theme }) => theme.colors.primary};
  padding-bottom: 1rem;
`;

const MobileLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  color: rgba(255, 255, 255, 0.88);
  text-decoration: none;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.radiusSm};
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
`;

const MobileRegionGrid = styled.div`
  margin: 0.6rem 0;
  padding: 0.35rem 0.25rem 0.15rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.45rem;
`;

const MobileRegionBtn = styled.button`
  border: none;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  cursor: pointer;
  padding: 0.45rem 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }
`;

const REGION_LABELS = {
  us: 'United States',
  gb: 'United Kingdom',
  au: 'Australia',
  ie: 'Ireland',
  de: 'Germany',
  at: 'Austria',
  es: 'Spain',
  it: 'Italy',
  fr: 'France',
  pt: 'Portugal',
  nl: 'Netherlands',
  no: 'Norway',
  fi: 'Finland',
  dk: 'Denmark',
  se: 'Sweden',
};

function flagUrl(code) {
  return `https://flagcdn.com/w40/${String(code || 'us').toLowerCase()}.png`;
}

function toRegionPath(pathname, targetRegion) {
  const parts = String(pathname || '').split('/').filter(Boolean);
  const current = regionFromPathname(pathname);
  if (current) parts.shift();
  if (targetRegion && targetRegion !== 'us') parts.unshift(targetRegion);
  return `/${parts.join('/')}`.replace(/\/+$/, '') || '/';
}

export default function Navbar() {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const region = regionFromPathname(pathname);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [regionOpen, setRegionOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [q, setQ] = useState('');
  const userMenuRef = useRef(null);
  const dispatch = useDispatch();
  const auth = useSelector((s) => s.auth);
  const { t } = useI18n();

  const home = pathWithRegion('/', region);
  const searchPath = pathWithRegion('/search', region);
  const loginPath = pathWithRegion('/login', region);
  const dashboardPath = pathWithRegion('/dashboard', region);
  const publicProfilePath = auth.user?.username ? pathWithRegion(`/profile/${auth.user.username}`, region) : '';

  const centerLinks = [
    { to: similarTypePath('movies', region), label: t('nav.similarMovies', 'Similar movies'), icon: Film, isActive: () => pathname === similarTypePath('movies', region) },
    { to: similarTypePath('tv', region), label: t('nav.similarTv', 'Similar TV shows'), icon: Tv, isActive: () => pathname === similarTypePath('tv', region) },
    { to: similarTypePath('anime', region), label: t('nav.similarAnime', 'Similar anime'), icon: Sparkles, isActive: () => pathname === similarTypePath('anime', region) },
  ];

  const onSearch = (e) => {
    e.preventDefault();
    const t = q.trim();
    navigate(t ? `${searchPath}?q=${encodeURIComponent(t)}` : searchPath);
    setSearchOpen(false);
    setMobileOpen(false);
    setQ('');
  };

  const activeRegion = region || 'us';
  const switchRegion = (nextRegion) => {
    const base = toRegionPath(pathname, nextRegion);
    navigate(`${base}${search || ''}${hash || ''}`);
    setRegionOpen(false);
    setMobileOpen(false);
  };

  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onDoc = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [userMenuOpen]);

  const openUserMenu = () => {
    setUserMenuOpen((v) => !v);
    setRegionOpen(false);
  };

  const closeUserMenu = () => setUserMenuOpen(false);

  const userLabel =
    auth.user?.displayName?.trim() ||
    (auth.user?.username ? `@${auth.user.username}` : '') ||
    auth.user?.email ||
    'Account';
  const userSub = auth.user?.username ? `@${auth.user.username}` : auth.user?.email || '';
  const userInitial = (
    auth.user?.displayName ||
    auth.user?.username ||
    auth.user?.email ||
    '?'
  )
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <Nav>
      <NavInner>
        <LogoLink to={home} onClick={() => setMobileOpen(false)}>
          <LogoBox>F</LogoBox>
          <LogoText>FliqMatch</LogoText>
        </LogoLink>

        <DesktopMid>
          {centerLinks.map(({ to, label, icon: Icon, isActive }) => {
            const active = isActive();
            return (
              <NavLink key={label} to={to} $active={active}>
                <Icon size={15} style={{ verticalAlign: '-2px', marginRight: '0.15rem', opacity: 0.9 }} />
                {label}
              </NavLink>
            );
          })}
        </DesktopMid>

        <Toolbar>
          <DesktopOnly>
            <RegionWrap>
              <RegionBtn
                type="button"
                onClick={() => {
                  setRegionOpen((v) => !v);
                  setUserMenuOpen(false);
                }}
                aria-label="Switch region"
              >
                <Flag src={flagUrl(activeRegion)} alt={activeRegion} />
                {String(activeRegion).toUpperCase()}
              </RegionBtn>
              <AnimatePresence>
                {regionOpen && (
                  <RegionMenu
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.16 }}
                  >
                    {PUBLIC_REGION_CODES.map((code) => (
                      <RegionItem key={code} type="button" onClick={() => switchRegion(code)}>
                        <Flag src={flagUrl(code)} alt={code} />
                        <span>{REGION_LABELS[code] || String(code).toUpperCase()}</span>
                      </RegionItem>
                    ))}
                  </RegionMenu>
                )}
              </AnimatePresence>
            </RegionWrap>
            <SearchBtn type="button" aria-label="Search" onClick={() => setSearchOpen((o) => !o)}>
              <Search size={18} strokeWidth={2.5} />
            </SearchBtn>
          </DesktopOnly>
          {auth.isAuthenticated ? (
            <UserMenuWrap ref={userMenuRef}>
              <UserMenuBtn type="button" onClick={openUserMenu} aria-expanded={userMenuOpen} aria-haspopup="true" aria-label="Account menu">
                <UserAvatar>
                  {auth.user?.avatarUrl ? <img src={auth.user.avatarUrl} alt="" /> : userInitial}
                </UserAvatar>
                <ChevronDown size={14} strokeWidth={2.5} style={{ opacity: 0.85 }} />
              </UserMenuBtn>
              <AnimatePresence>
                {userMenuOpen && (
                  <UserMenuPanel
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.16 }}
                  >
                    <UserMenuHeader>
                      <UserMenuName>{userLabel}</UserMenuName>
                      {userSub ? <UserMenuSub>{userSub}</UserMenuSub> : null}
                    </UserMenuHeader>
                    <UserMenuRow to={dashboardPath} onClick={closeUserMenu}>
                      <LayoutDashboard size={16} strokeWidth={2} />
                      {t('nav.dashboard', 'Dashboard')}
                    </UserMenuRow>
                    {publicProfilePath ? (
                      <UserMenuRow to={publicProfilePath} onClick={closeUserMenu}>
                        <User size={16} strokeWidth={2} />
                        {t('nav.profile', 'Profile')}
                      </UserMenuRow>
                    ) : null}
                    <UserMenuButton
                      type="button"
                      onClick={() => {
                        closeUserMenu();
                        dispatch(logoutUser());
                        navigate(home);
                      }}
                    >
                      <LogOut size={16} strokeWidth={2} />
                      {t('nav.logout', 'Log out')}
                    </UserMenuButton>
                  </UserMenuPanel>
                )}
              </AnimatePresence>
            </UserMenuWrap>
          ) : (
            <DesktopOnly>
              <NavActionBtn to={loginPath}>{t('nav.login', 'Log in')}</NavActionBtn>
            </DesktopOnly>
          )}

          <MobileToggle type="button" aria-label="Menu" onClick={() => setMobileOpen((o) => !o)}>
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </MobileToggle>
        </Toolbar>
      </NavInner>

      <AnimatePresence>
        {searchOpen && (
          <SearchPanel
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <SearchForm onSubmit={onSearch}>
              <Container>
                <SearchInputWrap>
                  <Search
                    size={18}
                    style={{
                      position: 'absolute',
                      left: '0.85rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#64748b',
                    }}
                  />
                  <SearchInput
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t('navbar.searchPlaceholder', 'Search movie, TV show, or anime...')}
                    autoFocus
                  />
                  <SearchSubmit type="submit">{t('common.search', 'Search')}</SearchSubmit>
                </SearchInputWrap>
              </Container>
            </SearchForm>
          </SearchPanel>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mobileOpen && (
          <MobilePanel
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <Container>
              <MobileRegionGrid>
                {PUBLIC_REGION_CODES.map((code) => (
                  <MobileRegionBtn key={code} type="button" onClick={() => switchRegion(code)}>
                    <Flag src={flagUrl(code)} alt={code} />
                    {String(code).toUpperCase()}
                  </MobileRegionBtn>
                ))}
              </MobileRegionGrid>
              {centerLinks.map(({ to, label, icon: Icon }) => (
                <MobileLink key={label} to={to} onClick={() => setMobileOpen(false)}>
                  <Icon size={20} />
                  {label}
                </MobileLink>
              ))}
              {!auth.isAuthenticated ? (
                <MobileLink to={loginPath} onClick={() => setMobileOpen(false)}>
                  {t('nav.login', 'Log in')}
                </MobileLink>
              ) : null}
              <MobileLink to={searchPath} onClick={() => setMobileOpen(false)}>
                <Search size={20} />
                Search
              </MobileLink>
            </Container>
          </MobilePanel>
        )}
      </AnimatePresence>
    </Nav>
  );
}
